import React, { useContext, useMemo, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import MyContext from "../MyContext/MyContext";

const ProductDetailsPageOne = () => {
  const { productId, category } = useParams();
  const navigate = useNavigate();
  const { data, user, addToCart } = useContext(MyContext);

  // ---------------- MESSAGE SYSTEM ----------------
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // error | success

  // ---------------- GLOBAL TOAST CSS ----------------
  useEffect(() => {
    const style = document.createElement("style");
  style.innerHTML = `
  .toast-popup {
    position: fixed;
    top: 90px;
    left: 50%;
    transform: translateX(-50%);
    padding: 16px 24px;
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 10px 35px rgba(0,0,0,0.18);
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 99999;
    opacity: 0;
    transform: translate(-50%, -10px);
    animation: toastIn 0.35s ease-out forwards, toastFade 0.35s ease-in 2.1s forwards;
  }

  .toast-popup i {
    font-size: 20px;
  }

  .toast-popup.success {
    border-left: 5px solid #32c759;
    background: #e6f8ec;
    color: #0e7b37;
  }

  .toast-popup.error {
    border-left: 5px solid #ff3b30;
    background: #ffeaea;
    color: #b00020;
  }

  @keyframes toastIn {
    from { opacity: 0; transform: translate(-50%, -20px); }
    to   { opacity: 1; transform: translate(-50%, 0); }
  }

  @keyframes toastFade {
    from { opacity: 1; }
    to   { opacity: 0; }
  }
`;

    document.head.appendChild(style);
  }, []);

  // ---------------- FLATTEN PRODUCTS ----------------
  const allProducts = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.flatMap((cat) =>
      (cat.products || []).map((p) => ({
        ...p,
        routing_category: String(cat.routing_category || "").toLowerCase(),
      }))
    );
  }, [data]);

  // ---------------- FIND PRODUCT ----------------
  const product = useMemo(() => {
    if (!allProducts || !category || !productId) return null;
    return (
      allProducts.find(
        (p) =>
          String(p.id) === String(productId) &&
          String(p.routing_category).toLowerCase() ===
            String(category).toLowerCase()
      ) || null
    );
  }, [allProducts, category, productId]);

  // ---------------- STATE ----------------
  const [mainImage, setMainImage] = useState("");
  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState("");

  useEffect(() => {
    if (product) {
      const imgs =
        Array.isArray(product.images) && product.images.length
          ? product.images
          : product.image
          ? [product.image]
          : [];

      setMainImage(imgs.length ? imgs[0] : "");
      setQty(1);

      setSelectedColor(product.colors?.[0] || "");
    }
  }, [product]);

  const inc = () => setQty((q) => q + 1);
  const dec = () => setQty((q) => (q > 1 ? q - 1 : 1));

  // ---------------- SLIDER SETTINGS ----------------
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow:
      product?.images && product.images.length
        ? Math.min(product.images.length, 4)
        : 4,
    slidesToScroll: 1,
    focusOnSelect: true,
  };

  // ---------------- ADD TO CART ----------------
const handleAddToCart = () => {
  if (!user) {
    setMessage("Please log in to add items to your cart.");
    setMessageType("error");

    setTimeout(() => {
      navigate("/account");
    }, 1200);

    return; // 🔥 STOP right here — do NOT continue
  }

  // user IS logged in → safe to add to cart
  addToCart({
    productId: product.id,
    quantity: qty,
    color: selectedColor,
    price: product.price?.current,
    title: product.title,
    image: mainImage,
  });

  setMessage("Item added to cart!");
  setMessageType("success");

  setTimeout(() => {
    setMessage("");
  }, 1500);
};


  // ---------------- PRODUCT NOT FOUND ----------------
  if (!product) {
    return (
      <section className="py-80 text-center">
        <h2>Product Not Found</h2>
      </section>
    );
  }

  const productImages =
    Array.isArray(product.images) && product.images.length
      ? product.images
      : product.image
      ? [product.image]
      : [];

  const detailsEntries = Object.entries(product.details || {});

  const stockPercent = (() => {
    const q = Number(product?.quantity || 0);
    const max = Number(product?.max_quantity || product?.quantity || 1);
    if (!max) return 0;
    return Math.round((q / max) * 100);
  })();

  // ---------------- FULL UI ----------------
  return (
    <section className="product-details py-80">
      {/* TOAST MESSAGE */}
      {message && (
        <div className={`toast-popup ${messageType}`}>
          <i className="ph-bold ph-warning-circle" />
          {message}
        </div>
      )}

      <div className="container container-lg">
        <div className="row gy-4">

          {/* LEFT + RIGHT */}
          <div className="col-lg-9">
            <div className="row gy-4">

              {/* LEFT IMAGES */}
              <div className="col-xl-6">
                <div className="product-details__left">
                  <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                    <div>
                      <div className="product-details__thumb flex-center h-100">
                        <img src={mainImage} alt={product.title} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-24">
                    <Slider {...sliderSettings}>
                      {productImages.map((img, idx) => (
                        <div
                          key={idx}
                          className="center max-w-120 max-h-120 h-100 flex-center border border-gray-100 rounded-16 p-8 cursor-pointer"
                          onClick={() => setMainImage(img)}
                        >
                          <img className="thum" src={img} alt={`${product.title}-${idx}`} />
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>

              {/* RIGHT DETAILS */}
              <div className="col-xl-6">
                <div className="product-details__content">
                  <h5 className="mb-12">{product.title}</h5>

                  {/* Rating */}
                  <div className="flex-align flex-wrap gap-12">
                    <div className="flex-align gap-8">
                      {Array.from({
                        length: Math.round(product.rating?.stars || 0),
                      }).map((_, i) => (
                        <span key={i} className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                      ))}
                    </div>

                    <span className="text-sm fw-medium text-neutral-600">
                      {product.rating?.stars ?? 0} Star Rating
                    </span>

                    <span className="text-sm fw-medium text-gray-500">
                      ({product.rating?.reviews_count ?? 0})
                    </span>
                  </div>

                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />

                  <p className="text-gray-700">{product.fulldescription}</p>

                  {/* Price */}
                  <div className="mt-32 flex-align gap-32 flex-wrap">
                    <div className="flex-align gap-8">
                      <h4 className="mb-0">${Number(product.price?.current ?? 0)}</h4>

                      {product.price?.original && (
                        <span className="text-md text-gray-500">
                          ${Number(product.price.original)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Colors */}
                  {Array.isArray(product.colors) &&
                    product.colors.length > 0 && (
                      <>
                        <span className="text-gray-900 d-block mt-32 mb-8">Color:</span>

                        <div className="flex-align gap-12 flex-wrap mb-24">
                          {product.colors.map((color, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setSelectedColor(color)}
                              className={`py-6 px-20 rounded-pill border transition ${
                                selectedColor === color
                                  ? "bg-main-600 text-white border-main-600 shadow-sm"
                                  : "border-gray-300 text-gray-800 hover-bg-gray-100"
                              }`}
                            >
                              {color}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                  {/* STOCK */}
                  <div className="mb-24">
                    <div className="mt-32 flex-align gap-12 mb-16">
                      <span className="w-32 h-32 bg-white flex-center rounded-circle text-main-600 box-shadow-xl">
                        <i className="ph-fill ph-lightning" />
                      </span>
                      <h6 className="text-md fw-bold text-gray-900 mb-0">
                        Products are almost sold out
                      </h6>
                    </div>

                    <div className="progress w-100 bg-gray-100 rounded-pill h-8">
                      <div
                        className="progress-bar bg-main-two-600 rounded-pill"
                        style={{ width: `${stockPercent}%` }}
                      ></div>
                    </div>

                    <span className="text-sm text-gray-700 mt-8 d-block">
                      Available only: {product.quantity}
                    </span>
                  </div>

                  {/* QUANTITY + ADD TO CART */}
                  <span className="text-gray-900 d-block mb-8">Quantity:</span>

                  <div className="flex-between gap-16 flex-wrap">
                    <div className="flex-align gap-16 flex-wrap">

                      {/* Quantity control */}
                      <div className="border border-gray-100 rounded-pill py-9 px-16 flex-align">
                        <button
                          onClick={dec}
                          type="button"
                          className="quantity__minus p-4 text-gray-700 hover-text-main-600"
                        >
                          <i className="ph ph-minus" />
                        </button>

                        <input
                          type="number"
                          readOnly
                          value={qty}
                          className="quantity__input border-0 text-center w-32"
                        />

                        <button
                          onClick={inc}
                          type="button"
                          className="quantity__plus p-4 text-gray-700 hover-text-main-600"
                        >
                          <i className="ph ph-plus" />
                        </button>
                      </div>

                      {/* ADD TO CART BUTTON */}
                      <button
                        onClick={handleAddToCart}
                        className="btn btn-main rounded-pill d-inline-flex flex-align gap-8 px-48"
                      >
                        <i className="ph ph-shopping-cart" /> Add To Cart
                      </button>

                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* SIDEBAR */}
          <div className="col-lg-3">
            <div className="product-details__sidebar border border-gray-100 rounded-16 overflow-hidden">

              {/* Seller Box */}
              <div className="p-24">
                <div className="flex-between bg-main-600 rounded-pill p-8">
                  <div className="flex-align gap-8">
                    <span className="w-44 h-44 bg-white rounded-circle flex-center text-2xl">
                      <i className="ph ph-storefront" />
                    </span>
                    <span className="text-white">Sold by DurgaEnterprise</span>
                  </div>
                </div>
              </div>

              {/* Sidebar Blocks */}
              <div className="p-24 bg-color-one d-flex gap-24 border-bottom border-gray-100">
                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl">
                  <i className="ph-fill ph-truck" />
                </span>
                <div>
                  <h6 className="text-sm mb-8">Fast Delivery</h6>
                  <p className="text-gray-700">Lightning-fast shipping, guaranteed.</p>
                </div>
              </div>

              <div className="p-24 bg-color-one d-flex gap-24 border-bottom border-gray-100">
                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl">
                  <i className="ph-fill ph-arrow-u-up-left" />
                </span>
                <div>
                  <h6 className="text-sm mb-8">Free 90-day returns</h6>
                  <p className="text-gray-700">Shop risk-free with easy returns.</p>
                </div>
              </div>

              <div className="p-24 bg-color-one d-flex gap-24 border-bottom border-gray-100">
                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl">
                  <i className="ph-fill ph-check-circle" />
                </span>
                <div>
                  <h6 className="text-sm mb-8">Pickup available</h6>
                  <p className="text-gray-700">Usually ready in 24 hours.</p>
                </div>
              </div>

              <div className="p-24 bg-color-one d-flex gap-24 border-bottom border-gray-100">
                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl">
                  <i className="ph-fill ph-credit-card" />
                </span>
                <div>
                  <h6 className="text-sm mb-8">Payment</h6>
                  <p className="text-gray-700">Card, Google Pay, Cash on delivery.</p>
                </div>
              </div>

              <div className="p-24 bg-color-one d-flex gap-24">
                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl">
                  <i className="ph-fill ph-package" />
                </span>
                <div>
                  <h6 className="text-sm mb-8">Packaging</h6>
                  <p className="text-gray-700">R&D packaging ensures safe delivery.</p>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* DESCRIPTION TAB */}
        <div className="pt-80">
          <div className="product-dContent border rounded-24">

            <div className="product-dContent__header border-bottom border-gray-100 flex-between gap-16">
              <ul className="nav common-tab nav-pills mb-3">
                <li className="nav-item">
                  <button
                    className="nav-link active"
                    data-bs-toggle="pill"
                    data-bs-target="#desc"
                  >
                    Description
                  </button>
                </li>
              </ul>

              <Link
                to="#"
                className="btn bg-color-one flex-align rounded-16 gap-8 text-main-600 hover-bg-main-600 hover-text-white"
              >
                <img src="assets/images/icon/satisfaction-icon.png" alt="" />
                100% Satisfaction Guaranteed
              </Link>
            </div>

            <div className="product-dContent__box tab-content">
              <div className="tab-pane fade show active" id="desc">
                <div className="mb-40">
                  <h6 className="mb-24">Product Description</h6>
                  <p>{product.fulldescription}</p>
                </div>

                <div className="mb-40">
                  <h6 className="mb-24">Product Specifications</h6>

                  <ul className="mt-32">
                    {detailsEntries.map(([k, v], i) => (
                      <li key={i} className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {k}: <span className="text-gray-500">{v}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default ProductDetailsPageOne;
